import React, { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { IconSpinner, LayoutComposer, IntegratedSearchBar } from '../../components/index.js';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer.js';
import FooterContainer from '../FooterContainer/FooterContainer.js';
import HorizontalListingsGrid from '../../components/HorizontalListingsGrid/HorizontalListingsGrid';

import { validProps } from './Field';

import SectionBuilder from './SectionBuilder/SectionBuilder.js';
import StaticPage from './StaticPage.js';

import css from './PageBuilder.module.css';

const getMetadata = (meta, schemaType, fieldOptions) => {
  const { pageTitle, pageDescription, socialSharing } = meta;

  // pageTitle is used for <title> tag in addition to page schema for SEO
  const title = validProps(pageTitle, fieldOptions)?.content;
  // pageDescription is used for different <meta> tags in addition to page schema for SEO
  const description = validProps(pageDescription, fieldOptions)?.content;
  // Data used when the page is shared in social media services
  const openGraph = validProps(socialSharing, fieldOptions);
  // We add OpenGraph image as schema image if it exists.
  const schemaImage = openGraph?.images1200?.[0]?.url;
  const schemaImageMaybe = schemaImage ? { image: [schemaImage] } : {};
  const isArticle = ['Article', 'NewsArticle', 'TechArticle'].includes(schemaType);
  const schemaHeadlineMaybe = isArticle ? { headline: title } : {};

  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org (This template uses JSON-LD format)
  //
  // In addition to this schema data for search engines, src/components/Page/Page.js adds some extra schemas
  // Read more about schema:
  // - https://schema.org/
  // - https://developers.google.com/search/docs/advanced/structured-data/intro-structured-data
  const pageSchemaForSEO = {
    '@context': 'http://schema.org',
    '@type': schemaType || 'WebPage',
    description: description,
    name: title,
    ...schemaHeadlineMaybe,
    ...schemaImageMaybe,
  };

  return {
    title,
    description,
    schema: pageSchemaForSEO,
    socialSharing: openGraph,
  };
};

const LoadingSpinner = () => {
  return (
    <div className={css.loading}>
      <IconSpinner delay={600} />
    </div>
  );
};

//////////////////
// Page Builder //
//////////////////

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * PageBuilder can be used to build content pages using page-asset.json.
 *
 * Note: props can include a lot of things that depend on
 * - pageAssetsData: json asset that contains instructions how to build the page content
 *   - asset should contain an array of _sections_, which might contain _fields_ and an array of _blocks_
 *     - _blocks_ can also contain _fields_
 * - fallbackPage: component. If asset loading fails, this is used instead.
 * - options: extra mapping of 3 level of sub components
 *   - sectionComponents: { ['my-section-type']: { component: MySection } }
 *   - blockComponents: { ['my-component-type']: { component: MyBlock } }
 *   - fieldComponents: { ['my-field-type']: { component: MyField, pickValidProps: data => Number.isInteger(data.content) ? { content: data.content } : {} }
 *     - fields have this pickValidProps as an extra requirement for data validation.
 * - pageProps: props that are passed to src/components/Page/Page.js component
 *
 * @param {Object} props
 * @param {Object} props.pageAssetsData
 * @param {Array<Object>} props.pageAssetsData.sections
 * @param {Object} props.pageAssetsData.meta
 * @param {Object} props.pageAssetsData.meta.pageTitle
 * @param {Object} props.pageAssetsData.meta.pageDescription
 * @param {Object} props.pageAssetsData.meta.socialSharing
 * @param {boolean?} props.inProgress
 * @param {Object?} props.error
 * @param {ReactNode?} props.fallbackPage
 * @param {string} props.schemaType type from schema.org (e.g. 'Article', 'Website')
 * @param {string?} props.currentPage name of the current page based on route configuration
 * @param {Object} props.options
 * @param {Object<string,FieldComponentConfig>} props.options.fieldComponents custom field components
 * @returns {JSX.Element} page component
 */
const PageBuilder = props => {
  const {
    pageAssetsData,
    inProgress,
    error,
    fallbackPage,
    schemaType,
    options,
    currentPage,
    isLandingPage,
    showSearchResults,
    searchResults,
    ...pageProps
  } = props;

  const history = useHistory();
  const location = useLocation();

  const initialSearchValues = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const initialValues = {};

    for (const [key, value] of searchParams.entries()) {
      initialValues[key] = value;
    }

    return initialValues;
  }, [location.search]);

  const handleSearchSubmit = (searchParams) => {
    const urlParams = {};

    // Add keywords/location search
    if (searchParams.keywords) {
      urlParams.keywords = searchParams.keywords;
    }

    // Add all pub_ parameters (dynamic filters) directly
    Object.keys(searchParams).forEach(key => {
      if (key.startsWith('pub_') && searchParams[key]) {
        urlParams[key] = searchParams[key];
      }
    });

    // Add price range if provided
    if (searchParams.price) {
      urlParams.price = searchParams.price;
    } else if (searchParams.minPrice) {
      urlParams.price = `${searchParams.minPrice},${searchParams.maxPrice || 999}`;
    }

    // Add date range if provided
    if (searchParams.dates && searchParams.dates.startDate) {
      urlParams.dates = `${searchParams.dates.startDate}${searchParams.dates.endDate ? ',' + searchParams.dates.endDate : ''}`;
    }

    // Add guests if provided
    if (searchParams.guests) {
      urlParams.guests = searchParams.guests;
    }

    // Navigate to appropriate page with search parameters
    const currentUrl = window.location.pathname;
    const isCurrentlyOnLandingPage = currentUrl === '/';
    const hasSearchParams = Object.keys(urlParams).length > 0;
    const queryString = new URLSearchParams(urlParams).toString();

    if (isCurrentlyOnLandingPage) {
      // Always redirect to search page from landing page (with or without parameters)
      history.push(`/s${queryString ? '?' + queryString : ''}`);
    } else if (hasSearchParams) {
      // Navigate to current page with search parameters (for other pages)
      history.push(`${currentUrl}${queryString ? '?' + queryString : ''}`);
    }
  };

  if (!pageAssetsData && fallbackPage && !inProgress && error) {
    return fallbackPage;
  }

  // Page asset contains UI info and metadata related to it.
  // - "sections" (data that goes inside <body>)
  // - "meta" (which is data that goes inside <head>)
  const { sections = [], meta = {} } = pageAssetsData || {};
  const pageMetaProps = getMetadata(meta, schemaType, options?.fieldComponents);

  const layoutAreas = isLandingPage ? `
    topbar
    searchbar
    main
    footer
  ` : `
    topbar
    main
    footer
  `;
  return (
    <StaticPage {...pageMetaProps} {...pageProps}>
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {props => {
          const { Topbar, Searchbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage={currentPage} isLandingPage={isLandingPage} />
              </Topbar>
              {/* {isLandingPage && Searchbar && (
                <Searchbar className={css.searchbar}>
                  <div className={css.searchBarWrapper}>
                    <IntegratedSearchBar
                      onSubmit={handleSearchSubmit}
                      className={css.searchBar}
                      initialValues={initialSearchValues}
                    />
                  </div>
                </Searchbar>
              )} */}
              <Main as="main" className={css.main}>
                {sections.length === 0 && inProgress ? (
                  <LoadingSpinner />
                ) : (
                  <>
                    <SectionBuilder
                    sections={sections}
                    options={options}
                    onSubmit={handleSearchSubmit}
                    initialValues={initialSearchValues}
                    isLandingPage = {isLandingPage}
                    showSearchResults={showSearchResults}
                    searchResults={searchResults}
                    searchResultsPosition={0}
                    />
                  </>
                )}
              </Main>
              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </StaticPage>
  );
};

export { LayoutComposer, StaticPage, SectionBuilder };

export default PageBuilder;
